import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import Logo from "../components/Logo";
import { apiClient, formatApiError } from "../lib/auth";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError("Enter a valid email");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiClient.post("/auth/password-reset/request", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err, "Could not send reset link."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 relative overflow-hidden" data-testid="forgot-password-page">
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-[#1D6AFF]/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative max-w-md mx-auto px-5 pt-10 pb-16">
        <button
          data-testid="forgot-back"
          onClick={() => navigate("/login")}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </button>

        <div className="flex justify-center mb-7">
          <Logo size="lg" />
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl p-7 sm:p-8 shadow-[0_20px_50px_-15px_rgba(15,23,42,0.12)]">
          {!sent ? (
            <>
              <h1 className="font-display text-3xl font-bold mb-2 text-slate-900">Forgot your password?</h1>
              <p className="text-sm text-slate-500 mb-7">
                Enter the email on your Newground account and we'll send you a secure reset link. It expires in 30 minutes.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Email</label>
                  <div className={`relative flex items-center bg-slate-50 border ${error ? "border-red-400" : "border-slate-200"} rounded-xl px-3.5 py-3 focus-within:border-[#1D6AFF] focus-within:bg-white transition-all`}>
                    <Mail className="w-4 h-4 text-slate-400 mr-2.5 shrink-0" />
                    <input
                      data-testid="forgot-email"
                      type="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="jane@agency.com"
                      className="flex-1 bg-transparent outline-none text-slate-900 placeholder:text-slate-400 text-sm"
                    />
                  </div>
                  {error && <div data-testid="forgot-error" className="text-xs text-red-500 mt-1.5">{error}</div>}
                </div>

                <button
                  type="submit"
                  data-testid="forgot-submit"
                  disabled={busy}
                  className="w-full bg-[#1D6AFF] hover:bg-[#1552C6] disabled:opacity-70 text-white font-semibold rounded-xl py-3.5 transition-all shadow-[0_14px_30px_-8px_rgba(29,106,255,0.4)]"
                >
                  {busy ? "Sending…" : "Send reset link"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-sent" className="text-center py-2">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-50 border border-emerald-300 flex items-center justify-center mb-5">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Check your inbox</h1>
              <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                If an account exists for <span className="font-semibold text-slate-800">{email}</span>, we just sent a reset link.
                The link expires in 30 minutes.
              </p>
              <button
                onClick={() => navigate("/login")}
                className="text-sm text-[#1D6AFF] hover:underline font-medium"
              >
                Return to login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
